import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQueries, UseQueryResult } from '@tanstack/react-query'
import axios from 'axios'
import { blue300, green300 } from 'ext-ui/style/colors'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Dropdown, {
  DropdownItemProps,
  DropdownOption,
  MultiItemProps,
} from 'src/components/Dropdown/Dropdown'
import EmptyDropdownMessage from 'src/components/EmptyDropdownMessage/EmptyDropdownMessage'
import SvgLightningBolt from 'src/components/Svgs/SvgLightningBolt'
import { TagIcon } from 'src/components/TagIcon/TagIcon'
import { DropdownOptionDiv } from 'src/components/TextDropdown/TextDropdown'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { ImageNames } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns, { useTranslate } from 'src/localization/Trns'
import {
  activeAlphabeticalExpenseCategoryLabelFiltersBody,
  ExpenseCategoryLabelResponse,
  useQueryExpenseCategoryLabels,
} from 'src/queries/organizations/expenseCategories.queries'
import {
  defaultQueryCacheOptions,
  orgQueryKeys,
} from 'src/queries/queries.constants'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { selectCalculatedBackgroundLocation } from 'src/redux/selectors/router.selectors'
import styled, { css } from 'styled-components'

export const EXPENSE_CATEGORY_EMPTY_OPTION = 'EXPENSE_CATEGORY_EMPTY_OPTION'
export const EXPENSE_CATEGORY_PREDICT_OPTION = 'extend_predict_expense_label'

export interface ExpenseCategoryDropdownOptionDetails {
  expenseCategoryOption: ExpenseCategoryLabelResponse
}

const ItemDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-grow: 2;
  height: 60px;
  ${getFontCss(
    14,
    themeConstants.colors.font_primary,
    styleConstants.fontFamilies.regular,
  )};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ExpenseCategoryNoneDropdownItem: React.FC<{ className?: string }> = (
  props,
): React.ReactElement => {
  return (
    <ItemDiv
      className={props.className}
      css={css`
        ${getFontCss(
          14,
          styleConstants.colors.gray_darkest,
          styleConstants.fontFamilies.regular,
        )};
      `}
      data-testid="none"
    >
      <Trns source={'(none)'} />
    </ItemDiv>
  )
}

export type ExpenseCategoryDropdownOption =
  DropdownOption<ExpenseCategoryDropdownOptionDetails>

const getLabelDropdownItemNameColor = (
  label: ExpenseCategoryLabelResponse | undefined,
): string | undefined => {
  switch (label?.id) {
    case EXPENSE_CATEGORY_EMPTY_OPTION:
      return styleConstants.colors.gray_darkest
    case EXPENSE_CATEGORY_PREDICT_OPTION:
      return styleConstants.colors.purple
    default:
      return undefined
  }
}

const getStateColor = (
  normal: string,
  isSelected: boolean,
  isUpdated: boolean,
): string => {
  if (isUpdated) return green300
  if (isSelected) return blue300
  return normal
}

const getLabelDropdownItemLeftIcon = (
  details: ExpenseCategoryDropdownOptionDetails | undefined,
  isSelected: boolean,
  isUpdated: boolean,
): React.ReactElement | undefined => {
  switch (details?.expenseCategoryOption?.id) {
    case EXPENSE_CATEGORY_PREDICT_OPTION:
      return (
        <TagIcon
          icon={
            <SvgLightningBolt
              fill={getStateColor(
                styleConstants.colors.purple,
                isSelected,
                isUpdated,
              )}
              viewBox={'0 0 16 16'}
            />
          }
          backgroundColor={getStateColor(
            styleConstants.colors.purple_light,
            isSelected,
            isUpdated,
          )}
          borderWidth={1}
        />
      )
    default:
      return undefined
  }
}

const ExpenseCategoryDropdownItem: React.FC<
  DropdownItemProps<ExpenseCategoryDropdownOptionDetails>
> = (props): React.ReactElement => {
  const nameColor = getLabelDropdownItemNameColor(
    props.details?.expenseCategoryOption,
  )
  const leftIcon = getLabelDropdownItemLeftIcon(
    props.details,
    !!props.isSelected,
    !!props.isUpdated,
  )
  return (
    <ItemDiv
      className={props.className}
      css={css`
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        ${props.isRoot &&
        css`
          height: 100%;
        `}
        ${!!props.isUpdated &&
        css`
          ${getFontCss(
            14,
            themeConstants.colors.highlight,
            styleConstants.fontFamilies.regular,
          )};
        `}
        ${props.isSelected &&
        css`
          ${getFontCss(
            14,
            themeConstants.colors.cta,
            styleConstants.fontFamilies.medium,
          )};
        `}
        ${props.details?.expenseCategoryOption.subName !== undefined &&
        !props.isRoot &&
        css`
          margin-top: 10px;
        `}
      `}
    >
      <DropdownOptionDiv
        className={props.className}
        isRoot={props.isRoot}
        isUpdated={!!props.isUpdated}
        isSelected={props.isSelected}
        hasAdditionalText={
          props.details?.expenseCategoryOption.subName !== undefined
        }
        nameColor={nameColor}
      >
        {leftIcon}
        {props.details?.expenseCategoryOption.name || props.value}
      </DropdownOptionDiv>
      {props.details?.expenseCategoryOption.subName !== undefined && (
        <p
          css={css`
            overflow: hidden;
            padding-bottom: ${props.isRoot ? 5 : 15}px;
            ${getFontCss(
              12,
              styleConstants.colors.gray_darkest,
              styleConstants.fontFamilies.regular,
            )};

            ${props.isRoot &&
            css`
              height: 100%;
            `}

            ${!!props.isUpdated &&
            css`
              ${getFontCss(
                12,
                themeConstants.colors.highlight,
                styleConstants.fontFamilies.regular,
              )};
            `}
          `}
        >
          {props.details.expenseCategoryOption.subName}
        </p>
      )}
    </ItemDiv>
  )
}

const ExpenseCategoryMultiItem: React.FC<
  MultiItemProps<ExpenseCategoryDropdownOptionDetails>
> = (props): React.ReactElement => {
  return (
    <ItemDiv
      className={props.className}
      css={css`
        height: 100%;
      `}
    >
      {props.options.length === 1 &&
        (props.options[0].details?.expenseCategoryOption.name ||
          props.options[0].value)}
      {props.options.length > 1 && (
        <Trns
          source={'${selectedOption}, +${count} more'}
          expressions={{
            selectedOption:
              props.options[0].details?.expenseCategoryOption.name ||
              props.options[0].value,
            count: `${props.options.length - 1}`,
          }}
        />
      )}
    </ItemDiv>
  )
}

const expenseCategoryOptionFilter = (
  option: ExpenseCategoryDropdownOption,
  val: string,
): boolean =>
  option.details !== undefined
    ? option.details.expenseCategoryOption.name
        ?.toLowerCase()
        .includes(val.toLowerCase()) ||
      option.value.toLowerCase().includes(val.toLowerCase())
    : option.value.toLowerCase().includes(val.toLowerCase())

const getExpenseCategoryOptionFilterText = (
  option: ExpenseCategoryDropdownOption,
): string =>
  option.details?.expenseCategoryOption.name ??
  option.details?.expenseCategoryOption.id ??
  option.value

interface ExpenseCategoryDropdownProps {
  className?: string
  showUpdates?: boolean
  hideWasOnUpdate?: boolean
  isOptional?: boolean
  freeTextAllowed: boolean
  hasLabels: boolean
  baseValue?: string
  selectedValue?: string
  selectedValues?: Set<string>
  chooseOption: (val: string) => void
  placeholder?: string
  textInputPlaceholder: string
  fieldLabel?: string
  errors?: Array<React.ReactElement | string>
  validateNewOption?: (val: string) => boolean
  deselectAllOptions?: () => void
  expCatId: string
  explicitSelectNone?: boolean
  tooltip?: React.ReactElement
  hideToolipOnUpdate?: boolean
  selectedOptions?: ExpenseCategoryDropdownOption[]
  isMulti?: boolean
  onResetClick?: () => void
  'data-testid'?: string
  testIdOptionBox?: string
}

const ExpenseCategoryDropdown: React.FC<ExpenseCategoryDropdownProps> = (
  props,
): React.ReactElement => {
  const listSearchImage = useColorOrGrayscaleImage(ImageNames.ListSearch)
  const translate = useTranslate()

  const calculatedBackgroundLocation = useSelector(
    selectCalculatedBackgroundLocation,
  )

  // normalize search to a stable primitive (avoid undefined/'' flip)
  const [searchRaw, setSearchRaw] = useState<string>('')
  const normalizedSearch = searchRaw.trim().toLowerCase()

  // stable filters object
  const labelFilters = useMemo(
    () => ({
      ...activeAlphabeticalExpenseCategoryLabelFiltersBody,
      search: normalizedSearch,
    }),
    [normalizedSearch],
  )

  const { data, fetchNextPage, isFetching, isPending, complete } =
    useQueryExpenseCategoryLabels({
      expenseCategoryId: props.expCatId,
      filters: labelFilters,
      enabled: props.hasLabels,
      queryCacheOptions: defaultQueryCacheOptions,
      additionalKey: calculatedBackgroundLocation.pathname,
    })

  const selectedSet =
    !isPending && props.selectedValues
      ? props.selectedValues
      : props.selectedValue !== undefined
        ? new Set([props.selectedValue])
        : new Set<string>()

  // detail lookups for selected ids (avoid refetch churn)
  const requestConfig = useSelector(selectAuthedJsonApiRequestConfig)
  const results = useQueries({
    queries: Array.from(selectedSet)
      .filter(
        (val) =>
          val !== EXPENSE_CATEGORY_EMPTY_OPTION &&
          val !== EXPENSE_CATEGORY_PREDICT_OPTION,
      )
      .map((labelId) => ({
        queryKey: orgQueryKeys.expenseDataLabelDetail(props.expCatId, labelId),
        queryFn: async (): Promise<ExpenseCategoryLabelResponse> => {
          const resp = await axios.get<ExpenseCategoryLabelResponse>(
            `/expensedata/categories/${props.expCatId}/labels/${labelId}`,
            requestConfig,
          )
          return resp.data
        },
        enabled: props.hasLabels && selectedSet.size > 0,
      })),
    combine: (r: Array<UseQueryResult<ExpenseCategoryLabelResponse, Error>>) =>
      r
        .map((q) => q.data)
        .filter((x): x is ExpenseCategoryLabelResponse => !!x),
  })

  // Build a local, non-mutating merged list for rendering.
  const mergedData: ExpenseCategoryLabelResponse[] = useMemo(() => {
    const base = Array.isArray(data) ? [...data] : []

    // ensure selected label detail results are present at the top (no dups)
    for (const item of results) {
      if (item && !base.some((x) => x.id === item.id)) base.unshift(item)
    }

    // inject explicit "(none)" option if requested
    if (
      props.explicitSelectNone &&
      !base.some((x) => x.id === EXPENSE_CATEGORY_EMPTY_OPTION)
    ) {
      base.unshift({
        id: EXPENSE_CATEGORY_EMPTY_OPTION,
        code: EXPENSE_CATEGORY_EMPTY_OPTION,
        name: translate('(none)') || '',
        active: true,
        categoryId: props.expCatId,
      } as ExpenseCategoryLabelResponse)
    }
    return base
  }, [data, results, props.explicitSelectNone, props.expCatId])

  const baseSet =
    !isPending && props.baseValue !== undefined
      ? new Set([props.baseValue])
      : new Set<string>()

  const onChangeSingle = (v: string) => {
    props.chooseOption?.(v === EXPENSE_CATEGORY_EMPTY_OPTION ? '' : v)
  }

  if (!props.hasLabels && props.freeTextAllowed) {
    return (
      <TextInputField
        design={TextInputFieldDesigns.New}
        fieldLabel={props.fieldLabel}
        fieldLabelAriaLabel={props.fieldLabel}
        tooltip={props.tooltip}
        hideToolipOnUpdate={props.hideToolipOnUpdate}
        errors={props.errors}
        value={props.selectedValue}
        onChange={onChangeSingle}
        placeholder={props.textInputPlaceholder}
        showUpdates={props.showUpdates}
        hideWasOnUpdate={props.hideWasOnUpdate}
        baseValue={props.baseValue}
        isOptional={props.isOptional}
        css={css`
          &:nth-child(n + 2) {
            margin-top: 24px;
          }
        `}
      />
    )
  }

  return (
    <Dropdown
      data-testid={props['data-testid']}
      testIdOptionBox={props.testIdOptionBox}
      className={props.className}
      isOptional={props.isOptional}
      fieldLabel={props.fieldLabel}
      fieldLabelAriaLabel={props.fieldLabel}
      tooltip={props.tooltip}
      hideToolipOnUpdate={props.hideToolipOnUpdate}
      allowNoneProps={
        props.deselectAllOptions !== undefined && props.hasLabels
          ? {
              NoneDropdownItem: ExpenseCategoryNoneDropdownItem,
              deselectAllOptions: props.deselectAllOptions,
            }
          : undefined
      }
      onResetClick={props.onResetClick}
      customEmptyDropdownMessage={
        <EmptyDropdownMessage
          imageSrc={listSearchImage}
          title={<Trns source={'No active labels'} />}
          subtitle={
            <Trns
              source={'Looks like there are no active labels to choose from'}
            />
          }
        />
      }
      showUpdates={props.showUpdates}
      hideWasOnUpdate={props.hideWasOnUpdate}
      baseValues={baseSet}
      MultiItem={ExpenseCategoryMultiItem}
      DropdownItem={ExpenseCategoryDropdownItem}
      optionGroups={[
        {
          label: props.freeTextAllowed ? (
            <Trns source={'Existing Labels'} />
          ) : undefined,
          options: mergedData.map((opt) => ({
            value: opt.id,
            details: { expenseCategoryOption: opt },
          })),
        },
      ]}
      isMulti={!!props.isMulti}
      selectedValues={selectedSet}
      chooseOption={onChangeSingle}
      placeholder={props.placeholder}
      errors={props.errors}
      filter={expenseCategoryOptionFilter}
      getFilterText={getExpenseCategoryOptionFilterText}
      validateNewOption={props.validateNewOption}
      newOptionLabel={<Trns source={'New Label'} />}
      selectedOptions={props.selectedOptions}
      infiniteScrollProps={{
        fetch: (filterInput?: string): void => {
          const next = (filterInput ?? '').trim().toLowerCase()
          if (next !== normalizedSearch) {
            setSearchRaw(next)
          } else {
            fetchNextPage()
          }
        },
        page: 0,
        loading: isFetching,
        complete: complete,
      }}
    />
  )
}

export default ExpenseCategoryDropdown
